import { z } from "zod/v4"
import { DefaultLookIdSchema, FriendCodeSchema, ItemIdSchema, SelectionIdSchema, SpaceKeySchema, UserId, UserIdSchema, UserVrmRegistrationIdSchema } from "../id"
import { DocumentSnapshotLike } from "../firestore"
import { CreatedAt, UpdatedAt } from "./system"
import { validateUserFreeInputText } from "../user-free-input-text-constraint"
import { SubscriptionKindNormal, SubscriptionKindPremium, SubscriptionKindPremiumPlus } from "./subscription/subscription-kind"
import { UserInventoryReason } from "./user-inventory"

export const UserDocumentMeta = z.object({
  name: z.string().describe("ニックネーム"),
  alias: z.string().describe("二つ名"),
  friend_code: FriendCodeSchema,
  description: z.string().optional().describe("自己紹介文。最大160文字。"),
})

export type UserDocumentMeta = z.infer<typeof UserDocumentMeta>

export const LookKindDefault = z.literal("DefaultLook")
export const LookKindInventory = z.literal("Inventory")
export const LookKindVirtualCast = z.literal("VC")

export const LookKind = z.union([
  LookKindDefault,
  LookKindInventory,
  LookKindVirtualCast,
])

const UpdateUserLookDefaultRequest = z.object({
  id: DefaultLookIdSchema,
  kind: LookKindDefault,
  customize: z.object({
    skinColor: z.number(),
    eyeColor: z.number(),
  })
})

const UpdateUserLookInventoryRequest = z.object({
  id: ItemIdSchema,
  kind: LookKindInventory,
})

const UpdateUserLookVirtualCastRequest = z.object({
  id: UserVrmRegistrationIdSchema,
  kind: LookKindVirtualCast,
})

export const UpdateUserLookRequest = z.union([
  UpdateUserLookDefaultRequest,
  UpdateUserLookInventoryRequest,
  UpdateUserLookVirtualCastRequest,
])

export type UpdateUserLookRequest = z.infer<typeof UpdateUserLookRequest>

export const UserDocumentLookFallback = z.object({
  id: ItemIdSchema,
  kind: LookKindInventory,
  name: z.string(),
  icon: z.string().optional(),
}).describe("VC連携VRMが利用不可になった際に表示する代替ルック。InitialLook由来。")

export type UserDocumentLookFallback = z.infer<typeof UserDocumentLookFallback>

/**
 * Firestore に保存する VC ルックのスキーマ。
 *
 * `soul_id` は VRM 登録ドキュメント（`vrm-registrations/{id}`）から非正規化してコピーされる。
 * API リクエストには含まれず、ルック設定時に登録の soul_id を読み取って保存する。
 */
const UserDocumentLookVirtualCast = UpdateUserLookVirtualCastRequest
  .extend({
    soul_id: z.string().optional().describe("魂のID。VRM登録から非正規化。"),
  })

export const UserDocumentLook = z.intersection(
  z.union([
    UpdateUserLookDefaultRequest,
    UpdateUserLookInventoryRequest,
    UserDocumentLookVirtualCast,
  ]),
  z.object({
    name: z.string().describe("ルックの名前"),
    icon: z.string().optional().describe("ルックのサムネイル"),
    reason: UserInventoryReason.optional().describe("ルックの取得理由。kind が Inventory の場合のみ設定される。purchase: ショップ購入、present: プレゼント/補填、initial-look: 初回選択、subscription: プレミアム会員特典、admin-grant: 管理者による強制付与"),
    model_number: z.string().nullable().optional().describe("型番。kind が Inventory かつ reason が purchase の場合のみ設定される。"),
    file_id: z.string().optional().describe("TSO のファイルID。kind が VC の場合のみ設定される。Unity 側が /files/{file_id} で VRM バイナリを取得する際に使用する。"),
    fallback_look: UserDocumentLookFallback.optional().describe("VC連携VRMが利用不可になった際に表示する代替ルック。kind が VC の場合のみ設定される。"),
  })
)

const UserSubscriptionPublic = z.object({
  kind: z.union([
    SubscriptionKindNormal,
    SubscriptionKindPremium,
    SubscriptionKindPremiumPlus,
  ]),
})

export type UserSubscriptionPublic = z.infer<typeof UserSubscriptionPublic>

export const UserStatusActive = z.literal("active")
export const UserStatusBanned = z.literal("banned")

export const UserStatus = z.union([
  UserStatusActive,
  UserStatusBanned,
])

export type UserStatus = z.infer<typeof UserStatus>

export const UserDocumentBody = z.intersection(
  UserDocumentMeta,
  z.object({
    look: UserDocumentLook.optional(),
    connection: z.object({
      virtualcast: z.boolean().optional(),
      virtualcast_disconnected_reason: z.enum(["user_initiated", "token_expired", "replaced"]).optional()
        .describe("virtualcast が false の場合のみ設定。解除理由。true に戻す際にクリアする。replaced: 同じ Virtualcast アカウントが別の POPOPO アカウントで連携されたため解除。"),
    }).optional().describe("外部アカウントとの連携有無。"),
    icon_url: z.string().optional().describe("アイコンの URL。クエリパラメータとして更新時間のタイムスタンプがつきます。"),
    icon_updated_at: z.number().optional().describe("アイコンの更新日時。"),
    first_nominated_selection_id: SelectionIdSchema.optional().describe("初めてノミネートされた抽選のID。"),
    online_space_id: SpaceKeySchema.optional().nullable().describe("参加中のスペースID(status=onlineの場合)。"),
    // TODO: 全ユーザーにsubscriptionが追加されたらoptionalを外す
    subscription: UserSubscriptionPublic.optional().describe("現在のサブスクリプション情報。"),
    initial_look_item_id: ItemIdSchema.optional().nullable().describe("初回起動時に選択したホロスーツのアイテムID。"),
    verified: z.boolean().optional().describe("認証済みフラグ。trueの場合、認証バッジが表示される。"),
    status: UserStatus.optional().describe("ユーザーステータス。未設定の場合は active として扱う。"),
    following_count: z.number().optional().describe("フォロー中のユーザー数。"),
    followers_count: z.number().optional().describe("フォロワー数。"),
    look_count: z.number().optional().describe("ホロスーツの保有数。"),
    created_at: CreatedAt,
    updated_at: UpdatedAt,
  })
)

export type UserDocumentBody = z.infer<typeof UserDocumentBody>

export type UserDocument = UserDocumentBody & {
  id: UserId,
}

export class Users {

  public static readonly NAME_MIN_LENGTH = 1
  public static readonly NAME_MAX_LENGTH = 32

  public static readonly ALIAS_MIN_LENGTH = 1
  public static readonly ALIAS_MAX_LENGTH = 30

  public static readonly DESCRIPTION_MAX_LENGTH = 160

  /**
   * VC連携VRMの最大サイズ（バイト）。
   * /items/{itemId} で取得した size がこの値を超えるVRMは、ルック変更時に拒否する。
   */
  public static readonly VC_VRM_MAX_SIZE_BYTES = 30 * 1024 * 1024

  /**
   * VRM 登録のクールダウン（ミリ秒）。
   * 直近の VRM 登録から本値以下の経過時間で再登録しようとした場合は拒否する。
   * `users/{userId}/vrm-registrations/{registrationId}` の `registered_at` を基準に判定する。
   */
  public static readonly VRM_REGISTRATION_COOLDOWN_MS = 10 * 60 * 1000

  public static toUserDenormalizedWithLook(user: UserDocument): UserDenormalizedWithLook {
    return {
      id: user.id,
      name: user.name,
      alias: user.alias,
      icon: user.icon_url,
      description: user.description,
      look: user.look ?? null,
      first_nominated_selection_id: user.first_nominated_selection_id,
      online_space_id: user.online_space_id,
      verified: user.verified,
      status: user.status,
      created_at: user.created_at,
      updated_at: user.updated_at,
    }
  }

  public static toUserDenormalizedWithoutLook(user: UserDocument): UserDenormalizedWithoutLook {
    return {
      id: user.id,
      name: user.name,
      alias: user.alias,
      icon: user.icon_url,
      description: user.description,
      first_nominated_selection_id: user.first_nominated_selection_id,
      online_space_id: user.online_space_id,
      verified: user.verified,
      status: user.status,
      created_at: user.created_at,
      updated_at: user.updated_at,
    }
  }

  /**
   * Firestore のドキュメントデータから UserDenormalizedWithoutLook を復元する。
   * Follow, LivePowerSummary, SpaceUser 等の fromDocumentSnapshot で共通利用する。
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public static parseDenormalizedWithoutLook(data: Record<string, any>): UserDenormalizedWithoutLook {
    return {
      id: data["id"],
      name: data["name"],
      alias: data["alias"],
      icon: data["icon"],
      description: data["description"],
      first_nominated_selection_id: data["first_nominated_selection_id"],
      online_space_id: data["online_space_id"],
      verified: data["verified"],
      created_at: data["created_at"],
      updated_at: data["updated_at"],
    }
  }

  /**
   * Firestore のドキュメントデータから UserDenormalizedWithLook を復元する。
   * SpaceUser 等の fromDocumentSnapshot で共通利用する。
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public static parseDenormalizedWithLook(data: Record<string, any>): UserDenormalizedWithLook {
    return {
      ...Users.parseDenormalizedWithoutLook(data),
      look: data["look"],
    }
  }
}

export const UpdateUserProfileRequest = z.object({
  name: validateUserFreeInputText({minLength: Users.NAME_MIN_LENGTH, maxLength: Users.NAME_MAX_LENGTH}).optional(),
  alias: validateUserFreeInputText({minLength: Users.ALIAS_MIN_LENGTH, maxLength: Users.ALIAS_MAX_LENGTH}).optional(),
  description: z.string().trim().max(Users.DESCRIPTION_MAX_LENGTH).optional().nullable(),
})

export type UpdateUserProfileRequest = z.infer<typeof UpdateUserProfileRequest>

export class UserDocuments {

  static toDocumentData(user: UserDocument): object {
    return {
      ...user,
      // id は document path に入るので入れない
      id: undefined,
    }
  }

  static fromDocumentSnapshot(doc: DocumentSnapshotLike | null): UserDocument | null {
    if (!doc) {
      return null
    }
    const data = doc.data()
    if (data === undefined) {
      return null
    }

    return {
      id: UserId(doc.id),
      name: data["name"],
      alias: data["alias"],
      icon_url: data["icon_url"],
      icon_updated_at: data["icon_updated_at"],
      look: data["look"],
      connection: data["connection"],
      friend_code: data["friend_code"],
      first_nominated_selection_id: data["first_nominated_selection_id"],
      online_space_id: data["online_space_id"],
      subscription: data["subscription"],
      initial_look_item_id: data["initial_look_item_id"],
      verified: data["verified"],
      status: data["status"],
      following_count: data["following_count"],
      followers_count: data["followers_count"],
      look_count: data["look_count"],
      description: data["description"],
      created_at: data["created_at"],
      updated_at: data["updated_at"],
    }
  }
}

export const UserDenormalizedWithoutLook = z.object({
  id: UserIdSchema,
  name: z.string().describe("ニックネーム。"),
  alias: z.string().describe("二つ名。"),
  icon: z.string().optional().describe("アイコンのURL。"),
  description: z.string().optional().describe("自己紹介文。"),
  first_nominated_selection_id: SelectionIdSchema.optional().describe("初めてノミネートされた抽選のID。"),
  online_space_id: SpaceKeySchema.optional().nullable().describe("参加中のスペースID (status=onlineの場合)。"),
  verified: z.boolean().optional().describe("認証済みフラグ。trueの場合、認証バッジが表示される。"),
  status: UserStatus.optional().describe("ユーザーステータス。未設定の場合は active として扱う。"),
  created_at: CreatedAt.optional().describe("ユーザーの登録日時 (UNIX Timestamp mills)。"),
  updated_at: UpdatedAt.optional().describe("ユーザーの更新日時 (UNIX Timestamp mills)。"),
}).describe("ルック情報を含まない非正規化されたユーザー情報。このデータの更新有無やタイミングは箇所によって異なります。")

export type UserDenormalizedWithoutLook = z.infer<typeof UserDenormalizedWithoutLook>

export const UserDenormalizedWithLook = z.intersection(UserDenormalizedWithoutLook, z.object({
  look: UserDocumentLook.nullable().describe("ルック情報。"),
})).describe("ルック情報を含む非正規化されたユーザー情報。このデータの更新有無やタイミングは箇所によって異なります。")

export type UserDenormalizedWithLook = z.infer<typeof UserDenormalizedWithLook>

export const UpdateUserPushSettingRequest = z.object({
  acceptPushOfOthers: z.boolean().optional(),
  acceptPushOfPrivateNotice: z.boolean().optional(),
  acceptPushOfGeneralNotice: z.boolean().optional(),
  acceptPushOfFollowingUserActions: z.boolean().optional(),
  acceptPushOfHistory: z.boolean().optional(),
  acceptCall: z.boolean().optional(),
  acceptSuperCall: z.boolean().optional(),
  acceptVoiceReplyPush: z.boolean().optional()
})

export type UpdateUserPushSettingRequest = z.infer<typeof UpdateUserPushSettingRequest>

export const CreateAccountRequest = z.object({
  withAgreementTermsOfService: z.boolean().optional().default(false),
  withAgreementPrivacy: z.boolean().optional().default(false),
})

export type CreateAccountRequest = z.infer<typeof CreateAccountRequest>
